// Preview share for a creator, rendered on the dark display well — the
// certificate as it would be issued today (portrait, hello text, real
// signature ink). Data comes from the admin-or-owner gated
// GET /api/v1/creators/{handle} via usePublicCreatorProfile (React
// Query dedupes with any sibling consumer on the same page). Shared by
// the composer's Step-2 quick-view dialog and the portfolio-application
// detail page. Placeholder conventions match the application-form
// preview: preferred tier, mint, zeroed serial, "New Dossier", today.

import { PatronageCertificate, Skeleton } from "@greenhold/components";

import { usePublicCreatorProfile } from "./usePublicCreatorProfile";

export function CreatorPreviewShare({
  handle,
  fallbackName,
  width = 240,
}: {
  handle: string | null;
  /** Name on the certificate when the profile has no display_name. */
  fallbackName: string;
  width?: number;
}) {
  const profileQuery = usePublicCreatorProfile(handle);

  if (!handle) {
    return (
      <p className="text-sm opacity-70">
        This creator has no handle yet, so there&apos;s no profile to
        preview a certificate from.
      </p>
    );
  }
  if (profileQuery.isPending) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }
  if (profileQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t load the profile: {profileQuery.error.message}
      </p>
    );
  }

  const profile = profileQuery.data;
  return (
    <div className="flex justify-center rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 px-4 py-6">
      <PatronageCertificate
        width={width}
        tier="preferred"
        name={profile.display_name?.trim() || fallbackName}
        serial="GH-0000-0000"
        dossier="New Dossier"
        issued={new Date().toISOString().slice(0, 10)}
        portraitUrl={profile.image_signed_url}
        signatureUrl={profile.signature_signed_url}
        quote={profile.hello_text ?? undefined}
        condition={0}
      />
    </div>
  );
}
